type ScriptToLoad = {
  src: string;
  isLoaded: boolean;
  afterLoad: (() => void)[];
};

export default class ScriptsLoader {
  static scriptsToLoad: ScriptToLoad[] = [];

  static loadScript(
    src: string,
    onLoadCallback: (() => void) | undefined = undefined,
    retryCount: number = 0,
  ): void {
    const script = ScriptsLoader.alreadyLoading(src);

    if (script !== undefined) {
      if (script.isLoaded) {
        if (onLoadCallback !== undefined) {
          onLoadCallback();
        }

        return;
      }

      if (onLoadCallback !== undefined) {
        script.afterLoad.push(onLoadCallback);
      }

      return;
    }

    const exists = ScriptsLoader.existsScriptInHeader(src);

    if (exists) {
      if (onLoadCallback !== undefined) {
        onLoadCallback();
      }

      return;
    }

    const scriptToLoad: ScriptToLoad = {
      src: src,
      isLoaded: false,
      afterLoad: [],
    };

    scriptToLoad.afterLoad.push(() => {
      console.debug("Loaded script: " + src);
    });

    if (onLoadCallback !== undefined) {
      scriptToLoad.afterLoad.push(onLoadCallback);
    }

    ScriptsLoader.scriptsToLoad.push(scriptToLoad);

    const newScript = document.createElement("script");
    newScript.src = scriptToLoad.src;
    newScript.async = true;

    document.getElementsByTagName("head")[0].appendChild(newScript);

    newScript.onload = function () {
      scriptToLoad.isLoaded = true;
      scriptToLoad.afterLoad.forEach((callable) => {
        callable();
      });
    };

    newScript.onerror = function () {
      if (retryCount > 5) {
        console.error("Unable to load script [" + retryCount + "]: " + src);

        throw new Error("Unable to load script [" + retryCount + "]: " + src);
      }

      console.warn(
        "Unable to load script [" + retryCount + "]: " + src + " Retrying...",
      );

      ScriptsLoader.scriptsToLoad.splice(
        ScriptsLoader.scriptsToLoad.findIndex((item) => {
          return item.src === src;
        }),
        1,
      );
      const script = Array.from(document.querySelectorAll("script")).find(
        (item) => {
          return item.src === src;
        },
      );
      script?.remove();

      setTimeout(() => {
        ScriptsLoader.loadScript(src, onLoadCallback, retryCount + 1);
      }, 50);
    };
  }

  static existsScriptInHeader(src: string) {
    const script = Array.from(document.querySelectorAll("script")).find(
      (item) => {
        return item.src === src;
      },
    );

    return script !== undefined;
  }

  static existsCssInHeader(src: string) {
    const script = Array.from(document.querySelectorAll("link")).find(
      (item) => {
        return item.href === src;
      },
    );

    return script !== undefined;
  }

  static alreadyLoading(src: string) {
    return ScriptsLoader.scriptsToLoad.find((item) => {
      return item.src === src;
    });
  }

  static loadCss(
    src: string,
    onLoadCallback: (() => void) | undefined = undefined,
  ) {
    const script = ScriptsLoader.alreadyLoading(src);

    if (script !== undefined) {
      if (script.isLoaded) {
        if (onLoadCallback !== undefined) {
          onLoadCallback();
        }

        return;
      }

      if (onLoadCallback !== undefined) {
        script.afterLoad.push(onLoadCallback);
      }

      return;
    }

    const exists = ScriptsLoader.existsCssInHeader(src);

    if (exists) {
      if (onLoadCallback !== undefined) {
        onLoadCallback();
      }

      return;
    }

    const scriptToLoad: ScriptToLoad = {
      src: src,
      isLoaded: false,
      afterLoad: [],
    };

    if (onLoadCallback !== undefined) {
      scriptToLoad.afterLoad.push(onLoadCallback);
    }

    ScriptsLoader.scriptsToLoad.push(scriptToLoad);

    const newCss = document.createElement("link");
    newCss.rel = "stylesheet";
    newCss.crossOrigin = "anonymous";
    newCss.type = "text/css";
    newCss.href = scriptToLoad.src;

    document.getElementsByTagName("head")[0].appendChild(newCss);

    newCss.onload = function () {
      scriptToLoad.isLoaded = true;
      scriptToLoad.afterLoad.forEach((callable) => {
        callable();
      });
    };

    newCss.onerror = function () {
      console.error("Unable to load css: " + src);
    };
  }
}
